// Region-aware date/time formatting. The region is the tenant's country (ISO code): "US" gets US
// conventions (month-first, 12-hour time, Sunday week start); every other supported region keeps the
// original UK-style output (day-first, 24-hour, English month names). Mirrors lib/money.ts.

export type DateInput = string | number | Date

// Time zones SuperAdmin can assign a tenant. Keep in sync with RegionFormats.TimeZones on the backend.
export const TIME_ZONES: { id: string; label: string }[] = [
  { id: 'Europe/London', label: 'London (GMT/BST)' },
  { id: 'Europe/Dublin', label: 'Dublin' },
  { id: 'Europe/Paris', label: 'Paris' },
  { id: 'Europe/Berlin', label: 'Berlin' },
  { id: 'Europe/Amsterdam', label: 'Amsterdam' },
  { id: 'Europe/Madrid', label: 'Madrid' },
  { id: 'Europe/Rome', label: 'Rome' },
  { id: 'America/New_York', label: 'US Eastern (New York)' },
  { id: 'America/Chicago', label: 'US Central (Chicago)' },
  { id: 'America/Denver', label: 'US Mountain (Denver)' },
  { id: 'America/Phoenix', label: 'US Arizona (no daylight saving)' },
  { id: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { id: 'America/Anchorage', label: 'US Alaska' },
  { id: 'Pacific/Honolulu', label: 'US Hawaii' },
]

/** The time zone to suggest when a tenant's country is chosen. */
export const DEFAULT_TIME_ZONE_FOR_COUNTRY: Record<string, string> = {
  GB: 'Europe/London', US: 'America/New_York', IE: 'Europe/Dublin', DE: 'Europe/Berlin',
  FR: 'Europe/Paris', NL: 'Europe/Amsterdam', ES: 'Europe/Madrid', IT: 'Europe/Rome',
}

export function isUs(region?: string | null): boolean {
  return region?.trim().toUpperCase() === 'US'
}

export function dateLocale(region?: string | null): string {
  return isUs(region) ? 'en-US' : 'en-GB'
}

/** 0 = Sunday (US), 1 = Monday (everywhere else we support). */
export function weekStartsOn(region?: string | null): 0 | 1 {
  return isUs(region) ? 0 : 1
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i

/**
 * Parses an API value. Date-only strings ("2026-09-18") and offset-less wall-clock strings
 * ("2026-09-18T00:00:00") are calendar values with no time zone; anything with an offset, a
 * number, or a Date is an instant.
 */
export function parseDate(value: DateInput): { date: Date; isInstant: boolean } {
  if (value instanceof Date) return { date: value, isInstant: true }
  if (typeof value === 'number') return { date: new Date(value), isInstant: true }
  if (DATE_ONLY.test(value)) return { date: new Date(value + 'T00:00:00'), isInstant: false }
  return { date: new Date(value), isInstant: HAS_OFFSET.test(value) }
}

// 12-hour US clocks read "6:30 PM", not "06:30 PM".
function adaptOptions(opts: Intl.DateTimeFormatOptions | undefined, region?: string | null): Intl.DateTimeFormatOptions | undefined {
  if (!opts) return opts
  return isUs(region) && opts.hour === '2-digit' ? { ...opts, hour: 'numeric' } : opts
}

export function formatDate(value: DateInput, region?: string | null, opts?: Intl.DateTimeFormatOptions, timeZone?: string): string {
  const { date, isInstant } = parseDate(value)
  return date.toLocaleDateString(dateLocale(region), { ...opts, ...(isInstant && timeZone ? { timeZone } : {}) })
}

export function formatTime(value: DateInput, region?: string | null, timeZone?: string): string {
  const { date, isInstant } = parseDate(value)
  const base: Intl.DateTimeFormatOptions = isUs(region)
    ? { hour: 'numeric', minute: '2-digit' }
    : { hour: '2-digit', minute: '2-digit' }
  return date.toLocaleTimeString(dateLocale(region), { ...base, ...(isInstant && timeZone ? { timeZone } : {}) })
}

export function formatDateTime(value: DateInput, region?: string | null, opts?: Intl.DateTimeFormatOptions, timeZone?: string): string {
  const { date, isInstant } = parseDate(value)
  return date.toLocaleString(dateLocale(region), { ...adaptOptions(opts, region), ...(isInstant && timeZone ? { timeZone } : {}) })
}

/** Today's calendar date ("YYYY-MM-DD") in a time zone, rather than the UTC date. */
export function todayIn(timeZone?: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone || 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
}

export type Dates = {
  region: string
  timeZone: string
  weekStartsOn: 0 | 1
  /** Today's date ("YYYY-MM-DD") on the tenant's calendar. */
  today: () => string
  formatDate: (value: DateInput, opts?: Intl.DateTimeFormatOptions) => string
  formatTime: (value: DateInput) => string
  formatDateTime: (value: DateInput, opts?: Intl.DateTimeFormatOptions) => string
}

/** Formatters bound to one tenant's region and time zone. Pure (not a React hook). */
export function datesFor(region?: string | null, timeZone?: string | null): Dates {
  const r = region?.trim().toUpperCase() || 'GB'
  const tz = timeZone || 'Europe/London'
  return {
    region: r,
    timeZone: tz,
    weekStartsOn: weekStartsOn(r),
    today: () => todayIn(tz),
    formatDate: (v, o) => formatDate(v, r, o, tz),
    formatTime: v => formatTime(v, r, tz),
    formatDateTime: (v, o) => formatDateTime(v, r, o, tz),
  }
}
